import * as React from 'react';
import * as styles from './styles.scss';
import { observer } from 'mobx-react-lite';
import { Icon } from '@fluentui/react/lib/Icon';
import {
    locationAssetLocationPlaceholder,
    assertErrorLocation,
    openDirectionInMaps,
    bingMapAlt,
    locationNameAriaText,
    addressAriaText,
    urlAriaText,
} from '../location.locstring.json';
import type { LocationPayload } from '../schema';
import loc from 'owa-localize';
import LocationCommonWell from 'owa-location-common-well/lib/components/LocationCommonWell';
import useLocationViewState from '../hooks/useLocationViewState';
import { ControlIcons } from 'owa-control-icons';
import { Link } from '@fluentui/react/lib/Link';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import classNames from 'classnames';
import { AssetErrorRetryable, AssetImage, PhoneNumber } from 'owa-timestream-asset-sdk';
import { KeyCodes } from '@fluentui/react/lib/Utilities';

/** Opens a new tab linking to the website of the place */
const openWebsite = (url: string) => () => {
    window.open(url, '_blank');
};

/** Opens a new tab linking to the directions of the place on enter key down */
const onKeyDownAddress = (url: string) => (event: React.KeyboardEvent<any>) => {
    if (event.which == KeyCodes.enter) {
        window.open(url, '_blank');
    }
};

export default observer(function LocationView({
    payload,
    DueDate,
}: {
    payload: LocationPayload;
    DueDate?: React.ReactNode;
}) {
    const {
        onLocationsChangedCallback,
        phoneNumber,
        businessUrl,
        directions,
        address,
        imageUrl,
        isLoading,
        pickerRef,
        isPickerVisible,
        setPickerVisible,
        errorType,
        onRetryGetPlace,
    } = useLocationViewState(payload);
    return (
        <div className={styles.locationDetailsContainer}>
            {imageUrl != '' && (
                <div className={styles.locationImage}>
                    <AssetImage
                        src={imageUrl}
                        onClick={openWebsite(directions)}
                        className={styles.locationImage}
                        title={loc(openDirectionInMaps)}
                        alt={loc(bingMapAlt, payload.selectedLocation?.persona.displayName)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={onKeyDownAddress(directions)}
                    />
                </div>
            )}
            <div className={styles.locationInfo}>
                <div className={styles.dueDateContainer}>{DueDate}</div>
                <div
                    className={classNames(styles.locationAssetTitle, {
                        [styles.noLocationAssetTitle]: !payload?.selectedLocation,
                        [styles.pickerLocationAssetTitle]: isPickerVisible,
                    })}>
                    <Icon iconName={ControlIcons.MapPin} className={styles.locationIcon} />
                    {isPickerVisible ? (
                        <div ref={pickerRef}>
                            <LocationCommonWell
                                containerClassName={styles.locationCommonWellContainer}
                                inputProps={{
                                    placeholder: loc(locationAssetLocationPlaceholder),
                                    'aria-label': loc(locationAssetLocationPlaceholder),
                                }}
                                onLocationsChanged={onLocationsChangedCallback}
                                defaultSelectedItems={
                                    payload?.selectedLocation ? [payload.selectedLocation] : []
                                }
                                draggableItemType={''}
                                itemLimit={1}
                                zeroQueryDisabled={true}
                                searchMode={'All'}
                                maxItemWidth={isLoading ? 100 : 110}
                            />
                        </div>
                    ) : (
                        <div
                            className={styles.locationName}
                            onClick={setPickerVisible}
                            onFocus={setPickerVisible}
                            tabIndex={0}
                            aria-label={
                                payload?.selectedLocation
                                    ? loc(
                                          locationNameAriaText,
                                          payload.selectedLocation?.persona.displayName
                                      )
                                    : loc(assertErrorLocation)
                            }>
                            {payload?.selectedLocation
                                ? payload.selectedLocation?.persona.displayName
                                : loc(assertErrorLocation)}
                        </div>
                    )}
                    {isLoading && <Spinner size={SpinnerSize.medium} />}
                </div>

                {!!payload?.selectedLocation && (
                    <div className={styles.locationDetailsContainer}>
                        <div className={classNames(styles.locationDetails, styles.addressDetails)}>
                            <Icon iconName={ControlIcons.World} className={styles.detailIcons} />
                            <Link
                                href={directions}
                                target="_blank"
                                aria-label={loc(addressAriaText, address)}
                                className={classNames(styles.detailRowText, styles.addressText)}>
                                {address}
                            </Link>
                        </div>
                        {!errorType ? (
                            <div>
                                <PhoneNumber
                                    phoneNumberText={phoneNumber}
                                    containerClassName={styles.locationDetails}
                                    iconClassName={styles.detailIcons}
                                    phoneNumberTextClassName={styles.detailRowText}
                                />
                                <div className={styles.locationDetails}>
                                    <Icon
                                        iconName={ControlIcons.Globe}
                                        className={styles.detailIcons}
                                    />
                                    <Link
                                        href={businessUrl}
                                        aria-label={loc(urlAriaText, businessUrl)}
                                        target="_blank"
                                        className={styles.detailRowText}>
                                        {businessUrl}
                                    </Link>
                                </div>
                            </div>
                        ) : (
                            <AssetErrorRetryable
                                errorDesc={loc(assertErrorLocation)}
                                retryCallback={onRetryGetPlace}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
});
